import { useEffect } from "react";

/**
 * The loading screen (`#veil` in index.html) stays until the page has loaded
 * whole, and then goes.
 *
 * Whole is: the document's load event, the fonts, every picture that is not
 * lazy, and every video that plays from the start — the hero's loop and the
 * animation under it — able to play through. The cards' loops and her call
 * loops are fetched later on purpose and are not waited for. A fetch that
 * fails counts as settled: a loading screen that waits on a missing file is a
 * page nobody reaches, so a hard cap stands behind all of it too.
 *
 * It is one attribute on <html>, which is what the stylesheet in index.html
 * fades the veil out on; the element is never removed, so the prerendered
 * markup ships it showing and the next visit starts the same way.
 */
const CAP_MS = 10_000;

export function useVeil() {
  useEffect(() => {
    let done = false;
    const lift = () => {
      if (done) return;
      done = true;
      document.documentElement.setAttribute("data-loaded", "");
    };
    const cap = setTimeout(lift, CAP_MS);
    void whole().then(lift);
    return () => clearTimeout(cap);
  }, []);
}

function whole(): Promise<unknown> {
  const waits: Promise<unknown>[] = [document.fonts.ready];

  if (document.readyState !== "complete") {
    waits.push(new Promise((r) => addEventListener("load", r, { once: true })));
  }

  for (const img of document.querySelectorAll<HTMLImageElement>("img[src]")) {
    if (img.loading === "lazy") continue;
    waits.push(img.decode().catch(() => undefined));
  }

  for (const v of document.querySelectorAll<HTMLVideoElement>("video")) {
    if (!v.src || v.preload === "none") continue;
    if (v.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) continue;
    waits.push(
      new Promise((r) => {
        v.addEventListener("canplaythrough", r, { once: true });
        v.addEventListener("error", r, { once: true });
      }),
    );
  }

  return Promise.all(waits);
}
